import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Modal } from '../components/Modal';
import { fmtKm, fmtMin, formatDate } from '../utils/mockState';
import { useToast } from '../components/Toast';
import api from '../api/client';
import { RouteEntity } from '../types';

export default function RoutesPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [routes, setRoutes] = useState<RouteEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('todas');
  const [periodFilter, setPeriodFilter] = useState('30d');
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState<'name' | 'driver' | 'stops' | 'distance' | 'duration' | 'savings'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [routeToDelete, setRouteToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get<RouteEntity[]>('/routes');
      setRoutes(data);
    } catch {
      showToast('Erro ao carregar rotas.', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (col: typeof sortCol) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const handleDuplicate = async (route: RouteEntity, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.post(`/routes/${route.id}/duplicate`);
      showToast(`Rota "${route.name}" duplicada com sucesso!`, 'success');
      await load();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Erro ao duplicar rota.', 'error');
    }
  };

  const handleDelete = (id: string, name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRouteToDelete({ id, name });
  };

  const confirmDelete = async () => {
    if (!routeToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/routes/${routeToDelete.id}`);
      showToast(`Rota "${routeToDelete.name}" excluída.`, 'info');
      setRouteToDelete(null);
      await load();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Erro ao excluir rota.', 'error');
    }
    finally {
      setDeleting(false);
    }
  };

  const filteredRoutes = useMemo(() => {
    const now = Date.now();
    const periodMs = periodFilter === 'hoje' ? 24 * 60 * 60 * 1000 : periodFilter === '7d' ? 7 * 24 * 60 * 60 * 1000 : 30 * 24 * 60 * 60 * 1000;

    return routes
      .filter((r) => {
        if (statusFilter !== 'todas' && r.status !== statusFilter) return false;
        if (now - new Date(r.date).getTime() > periodMs) return false;
        if (search) {
          const s = search.toLowerCase();
          const matchName = r.name.toLowerCase().includes(s);
          const matchDriver = (r.vehicle?.driverName || '').toLowerCase().includes(s);
          if (!matchName && !matchDriver) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const get = (r: RouteEntity) => {
          switch (sortCol) {
            case 'name': return r.name;
            case 'driver': return r.vehicle?.driverName || '';
            case 'stops': return r.stops?.length || 0;
            case 'distance': return r.distanceM;
            case 'duration': return r.durationS;
            case 'savings': return r.savingsPercent;
          }
        };
        const va: any = get(a);
        const vb: any = get(b);
        if (typeof va === 'string') {
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        }
        return sortDir === 'asc' ? (va || 0) - (vb || 0) : (vb || 0) - (va || 0);
      });
  }, [routes, statusFilter, periodFilter, search, sortCol, sortDir]);

  return (
    <Layout>
      <div className="page-head">
        <div>
          <h1 className="page-title">Rotas</h1>
          <p className="page-lede">
            Gerenciamento de rotas planejadas, em andamento e concluídas com histórico operacional completo.
          </p>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/frota/nova')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="1" y="3" width="15" height="13"></rect>
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
              <circle cx="5.5" cy="18.5" r="2.5"></circle>
              <circle cx="18.5" cy="18.5" r="2.5"></circle>
            </svg>
            Otimizar frota (VRP)
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/rotas/nova')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Nova rota
          </button>
        </div>
      </div>

      <div className="tbl-wrap">
        <div className="tbl-toolbar">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              className={`filter-chip ${statusFilter === 'todas' ? 'on' : ''}`}
              onClick={() => setStatusFilter('todas')}
            >
              Todas ({routes.length})
            </button>
            <button
              className={`filter-chip ${statusFilter === 'em_andamento' ? 'on' : ''}`}
              onClick={() => setStatusFilter('em_andamento')}
            >
              Em andamento
            </button>
            <button
              className={`filter-chip ${statusFilter === 'planejada' ? 'on' : ''}`}
              onClick={() => setStatusFilter('planejada')}
            >
              Planejadas
            </button>
            <button
              className={`filter-chip ${statusFilter === 'concluida' ? 'on' : ''}`}
              onClick={() => setStatusFilter('concluida')}
            >
              Concluídas
            </button>
            <button
              className={`filter-chip ${statusFilter === 'cancelada' ? 'on' : ''}`}
              onClick={() => setStatusFilter('cancelada')}
            >
              Canceladas
            </button>
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ position: 'relative', width: 220 }}>
              <input
                type="text"
                className="input"
                placeholder="Filtrar por nome, motorista..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ padding: '7px 10px', fontSize: 13 }}
              />
            </div>

            <div style={{ display: 'flex', gap: 4, background: 'var(--paper)', padding: 3, borderRadius: 8, border: '1px solid var(--line-2)' }}>
              <button
                className={`filter-chip ${periodFilter === 'hoje' ? 'on' : ''}`}
                onClick={() => setPeriodFilter('hoje')}
                style={{ padding: '4px 8px', fontSize: 11 }}
              >
                Hoje
              </button>
              <button
                className={`filter-chip ${periodFilter === '7d' ? 'on' : ''}`}
                onClick={() => setPeriodFilter('7d')}
                style={{ padding: '4px 8px', fontSize: 11 }}
              >
                7 dias
              </button>
              <button
                className={`filter-chip ${periodFilter === '30d' ? 'on' : ''}`}
                onClick={() => setPeriodFilter('30d')}
                style={{ padding: '4px 8px', fontSize: 11 }}
              >
                30 dias
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="empty"><p>Carregando rotas...</p></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data">
              <thead>
                <tr>
                  <th className={`sortable ${sortCol === 'name' ? `active ${sortDir}` : ''}`} onClick={() => handleSort('name')}>
                    Nome da rota
                    <span className="sort-ico">
                      <svg className="up" viewBox="0 0 10 6" fill="currentColor"><path d="M5 0L10 6H0z"/></svg>
                      <svg className="down" viewBox="0 0 10 6" fill="currentColor"><path d="M5 6L0 0h10z"/></svg>
                    </span>
                  </th>
                  <th>Veículo / CD</th>
                  <th className={`sortable ${sortCol === 'driver' ? `active ${sortDir}` : ''}`} onClick={() => handleSort('driver')}>
                    Motorista
                    <span className="sort-ico">
                      <svg className="up" viewBox="0 0 10 6" fill="currentColor"><path d="M5 0L10 6H0z"/></svg>
                      <svg className="down" viewBox="0 0 10 6" fill="currentColor"><path d="M5 6L0 0h10z"/></svg>
                    </span>
                  </th>
                  <th>Status</th>
                  <th className={`right sortable ${sortCol === 'stops' ? `active ${sortDir}` : ''}`} onClick={() => handleSort('stops')}>
                    Paradas
                    <span className="sort-ico">
                      <svg className="up" viewBox="0 0 10 6" fill="currentColor"><path d="M5 0L10 6H0z"/></svg>
                      <svg className="down" viewBox="0 0 10 6" fill="currentColor"><path d="M5 6L0 0h10z"/></svg>
                    </span>
                  </th>
                  <th className={`right sortable ${sortCol === 'distance' ? `active ${sortDir}` : ''}`} onClick={() => handleSort('distance')}>
                    Distância
                    <span className="sort-ico">
                      <svg className="up" viewBox="0 0 10 6" fill="currentColor"><path d="M5 0L10 6H0z"/></svg>
                      <svg className="down" viewBox="0 0 10 6" fill="currentColor"><path d="M5 6L0 0h10z"/></svg>
                    </span>
                  </th>
                  <th className={`right sortable ${sortCol === 'duration' ? `active ${sortDir}` : ''}`} onClick={() => handleSort('duration')}>
                    Duração
                    <span className="sort-ico">
                      <svg className="up" viewBox="0 0 10 6" fill="currentColor"><path d="M5 0L10 6H0z"/></svg>
                      <svg className="down" viewBox="0 0 10 6" fill="currentColor"><path d="M5 6L0 0h10z"/></svg>
                    </span>
                  </th>
                  <th className={`right sortable ${sortCol === 'savings' ? `active ${sortDir}` : ''}`} onClick={() => handleSort('savings')}>
                    Economia
                    <span className="sort-ico">
                      <svg className="up" viewBox="0 0 10 6" fill="currentColor"><path d="M5 0L10 6H0z"/></svg>
                      <svg className="down" viewBox="0 0 10 6" fill="currentColor"><path d="M5 6L0 0h10z"/></svg>
                    </span>
                  </th>
                  <th className="right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredRoutes.map((r) => {
                  const stopsCount = r.stops?.length || 0;
                  return (
                    <tr
                      key={r.id}
                      onClick={() => navigate(`/rotas/${r.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div style={{ fontWeight: 600, color: 'var(--teal-800)', fontSize: 14 }}>
                          {r.name}
                        </div>
                        <div style={{ color: 'var(--text-4)', fontSize: 11, marginTop: 2 }}>
                          {formatDate(r.date)} · Criada no sistema
                        </div>
                      </td>
                      <td>
                        <span className="mono" style={{ fontSize: 13, fontWeight: 600 }}>
                          {r.vehicle?.name || 'Veículo'}
                        </span>
                        <div style={{ color: 'var(--text-3)', fontSize: 11 }}>
                          {r.vehicle?.plate || '—'} · {r.depot?.name.split(' ')[0] || 'CD'}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{r.vehicle?.driverName || '—'}</div>
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            r.status === 'em_andamento'
                              ? 'b-amber'
                              : r.status === 'concluida'
                              ? 'b-teal'
                              : r.status === 'cancelada'
                              ? 'b-danger'
                              : 'b-gray'
                          }`}
                        >
                          <span className="dot"></span>
                          {r.status === 'em_andamento'
                            ? 'Em andamento'
                            : r.status === 'concluida'
                            ? 'Concluída'
                            : r.status === 'cancelada'
                            ? 'Cancelada'
                            : 'Planejada'}
                        </span>
                      </td>
                      <td className="right mono">{stopsCount}</td>
                      <td className="right mono">{fmtKm(r.distanceM / 1000)} km</td>
                      <td className="right mono">{fmtMin(r.durationS / 60)}</td>
                      <td className="right">
                        <span
                          style={{
                            color: r.savingsPercent > 0 ? '#0d7a6c' : 'var(--text-3)',
                            fontWeight: 600,
                            fontFamily: 'JetBrains Mono'
                          }}
                        >
                          {r.savingsPercent > 0 ? `-${r.savingsPercent.toFixed(1)}%` : '—'}
                        </span>
                      </td>
                      <td className="right">
                        <div className="row-actions">
                          {r.status === 'planejada' && (
                            <button
                              className="row-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/rotas/${r.id}/editar`);
                              }}
                              title="Editar rota planejada"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            </button>
                          )}
                          <button
                            className="row-btn"
                            onClick={(e) => handleDuplicate(r, e)}
                            title="Duplicar rota"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                          </button>
                          <button
                            className="row-btn"
                            onClick={(e) => handleDelete(r.id, r.name, e)}
                            title="Excluir rota"
                            style={{ color: 'var(--danger)' }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 6h18m-2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredRoutes.length === 0 && (
          <div className="empty">
            <h4>Nenhuma rota encontrada</h4>
            <p>Tente ajustar os filtros ou crie uma nova rota no botão acima.</p>
          </div>
        )}

        <Modal
          isOpen={routeToDelete !== null}
          onClose={() => !deleting && setRouteToDelete(null)}
          title="Excluir rota"
          maxWidth="440px"
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setRouteToDelete(null)} disabled={deleting}>
                Cancelar
              </button>
              <button className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Excluindo…' : 'Excluir rota'}
              </button>
            </>
          }
        >
          <p style={{ margin: 0, lineHeight: 1.55 }}>
            Tem certeza que deseja excluir a rota <strong>“{routeToDelete?.name}”</strong>?
          </p>
          <p style={{ margin: '10px 0 0', color: 'var(--text-3)', fontSize: 13 }}>
            Esta ação não pode ser desfeita.
          </p>
        </Modal>
      </div>
    </Layout>
  );
}
